import { Controller } from "@hotwired/stimulus"
import { showAiOverlay, hideAiOverlay } from "./ai_overlay"

export default class extends Controller {
  static targets = [
    "title", "author", "coverImage", "coverPlaceholder",
    "subtitle", "isbn13", "isbn10", "publisherName", "publicationDate",
    "pageCount", "language", "description", "coverImageUrl", "coverThumbnailUrl",
    "searchButton", "aiButton", "searchButtonText", "aiButtonText",
    "searchSpinner", "aiSpinner",
    "coverButton", "coverSpinner", "coverButtonText", "coverFileInput", "coverCameraInput",
    "coverDropZone", "coverDropOverlay",
    "authorsContainer", "authorRow", "authorInput",
    "previewSection", "previewTable",
  ]

  static values = {
    searchUrl: String,
    aiUrl: String,
    aiIdentifyUrl: String,
    aiCoverUrl: String,
    uploadCoverUrl: String,
    authorLookupUrl: String,
    publisherLookupUrl: String,
    authorImageUrl: String
  }

  // Toast notification helper
  showToast(message, type = "alert") {
    // Create toast container if it doesn't exist
    let container = document.querySelector('.toast-container')
    if (!container) {
      container = document.createElement('div')
      container.className = 'toast-container fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-3'
      document.body.appendChild(container)
    }

    const toast = document.createElement('div')
    toast.className = 'toast'
    toast.setAttribute('data-controller', 'flash')

    const icon = type === "notice"
      ? `<svg class="w-5 h-5 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`
      : `<svg class="w-5 h-5 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`

    toast.innerHTML = `
      <div class="flex items-center gap-3">
        ${icon}
        <span class="font-fira text-sm text-white">${this.escapeHtml(message)}</span>
        <button type="button" class="text-white/60 hover:text-white ml-2 shrink-0" data-action="flash#dismiss">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    container.appendChild(toast)

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      toast.classList.add('toast-exit')
      setTimeout(() => toast.remove(), 200)
    }, 5000)
  }

  async searchGoogleBooks(event) {
    event.preventDefault()
    await this.search("google_books")
  }

  async searchOpenLibrary(event) {
    event.preventDefault()
    await this.search("open_library")
  }

  async search(source = null) {
    const title = this.titleTarget.value.trim()
    const author = this.getFirstAuthorName()

    if (!title) {
      this.showToast("Please enter a book title first")
      return
    }

    this.setSearchLoading(true)

    try {
      const params = { title }
      if (author) params.author = author
      if (source) params.source = source

      const response = await fetch(this.searchUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(params)
      })

      const data = await response.json()

      if (response.ok) {
        this.fillForm(data)
      } else {
        this.showToast(data.error || "Book not found")
      }
    } catch (error) {
      console.error("Search error:", error)
      this.showToast("An error occurred while searching")
    } finally {
      this.setSearchLoading(false)
    }
  }

  get disambiguationController() {
    const el = document.getElementById("ai-disambiguation-dialog")
    if (!el) return null
    const app = this.application
    return app.getControllerForElementAndIdentifier(el, "ai-disambiguation")
  }

  async aiGenerate(event) {
    event.preventDefault()

    const title = this.titleTarget.value.trim()
    const author = this.getFirstAuthorName()

    if (!title) {
      this.showToast("Please enter a book title first")
      return
    }

    this.setAiLoading(true)

    try {
      if (this.hasAiIdentifyUrlValue && this.aiIdentifyUrlValue) {
        const identified = await this.identifyEntity({ title, author_name: author })

        if (identified === null) {
          // Fall back to direct generation on error/premium
          await this.directAiGenerate(title, author)
          return
        }

        if (identified === "cancelled") return

        if (identified.skip_disambiguation) {
          this.fillForm(identified.data)
          return
        }

        const disambiguation = this.disambiguationController
        if (!disambiguation) {
          await this.directAiGenerate(title, author)
          return
        }

        const selectedIndex = await disambiguation.show(identified.candidates, "book")
        if (!this.element.isConnected) return

        if (selectedIndex === null) return // user cancelled

        const selected = identified.candidates[selectedIndex]
        await this.directAiGenerate(selected.title || title, selected.author || author)
      } else {
        await this.directAiGenerate(title, author)
      }
    } catch (error) {
      console.error("AI generation error:", error)
      this.showToast("An error occurred while generating data")
    } finally {
      this.setAiLoading(false)
    }
  }

  async identifyEntity(params) {
    try {
      const response = await fetch(this.aiIdentifyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(params)
      })

      if (response.status === 402) {
        const data = await response.json()
        if (data.redirect_url) window.open(data.redirect_url, "_blank")
        return "cancelled"
      }

      if (!response.ok) {
        return null
      }

      const data = await response.json()

      if (!data.skip_disambiguation && (!data.candidates || data.candidates.length === 0)) {
        this.showToast("No matching book found")
        return "cancelled"
      }

      return data
    } catch (error) {
      console.error("Identify error:", error)
      return null
    }
  }

  async directAiGenerate(title, author) {
    const response = await fetch(this.aiUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ title, author_name: author })
    })

    const data = await response.json()

    if (response.ok) {
      this.fillForm(data)
    } else if (response.status === 402 && data.redirect_url) {
      window.open(data.redirect_url, "_blank")
    } else {
      this.showToast(data.error || "Failed to generate data")
    }
  }

  setBlurbLoading(isLoading) {
    if (this.hasBlurbButtonTarget) {
      this.blurbButtonTarget.disabled = isLoading
    }
    if (this.hasBlurbSpinnerTarget) {
      this.blurbSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasBlurbButtonTextTarget) {
      this.blurbButtonTextTarget.textContent = isLoading ? "Generating..." : "AI Generate"
    }
  }

  // Author management methods
  addAuthor(event) {
    event.preventDefault()
    const container = this.authorsContainerTarget
    const newRow = this.createAuthorRow("")
    container.appendChild(newRow)
    this.updateRemoveButtons()
    newRow.querySelector('input').focus()
  }

  removeAuthor(event) {
    event.preventDefault()
    const row = event.target.closest('[data-book-lookup-target="authorRow"]')
    if (row && this.authorRowTargets.length > 1) {
      row.remove()
      this.updateRemoveButtons()
    }
  }

  updateRemoveButtons() {
    const rows = this.authorRowTargets
    rows.forEach((row, index) => {
      const removeBtn = row.querySelector('[data-action="book-lookup#removeAuthor"]')
      if (removeBtn) {
        removeBtn.classList.toggle('hidden', rows.length === 1)
      }
    })
  }

  createAuthorRow(name) {
    const div = document.createElement('div')
    div.className = 'flex gap-2 items-center'
    div.setAttribute('data-book-lookup-target', 'authorRow')
    div.innerHTML = `
      <input type="text" name="author_names[]" value="${this.escapeHtml(name)}" class="form-input flex-1" placeholder="Author name" data-book-lookup-target="authorInput">
      <button type="button" data-action="book-lookup#removeAuthor" class="text-red-500 hover:text-red-700 p-1" title="Remove">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    return div
  }

  async lookupAuthor(event) {
    event.preventDefault()
    const row = event.target.closest('[data-book-lookup-target="authorRow"]')
    const input = row.querySelector('input[name="author_names[]"]')
    const name = input.value.trim()
    const button = event.target.closest('button')

    if (!name) {
      this.showToast("Please enter an author name first")
      return
    }

    this.showToast("Generating info for Author...", "notice")

    // Show loading state
    button.disabled = true
    const originalContent = button.innerHTML
    button.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`

    try {
      const response = await fetch(this.authorLookupUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      const data = await response.json()

      if (response.ok) {
        if (data.exists) {
          // Author already exists in user's library
          input.classList.add('border-green-500')
          input.title = `Existing author: ${data.author.name}`
          setTimeout(() => input.classList.remove('border-green-500'), 2000)
        } else if (data.author) {
          // New author data fetched via AI - use the name as-is
          input.classList.add('border-blue-500')
          input.title = 'Author info retrieved via AI'
          setTimeout(() => input.classList.remove('border-blue-500'), 2000)
        }
      } else {
        this.showToast(data.error || "Failed to lookup author")
      }
    } catch (error) {
      console.error("Author lookup error:", error)
      this.showToast("An error occurred while looking up author")
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  async lookupPublisher(event) {
    event.preventDefault()
    const name = this.publisherNameTarget?.value?.trim()
    const button = event.target.closest('button')

    if (!name) {
      this.showToast("Please enter a publisher name first")
      return
    }

    if (!this.hasPublisherLookupUrlValue) {
      this.showToast("Publisher lookup not configured")
      return
    }

    this.showToast("Generating info for Publisher...", "notice")

    // Show loading state
    button.disabled = true
    const originalContent = button.innerHTML
    button.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`

    try {
      const response = await fetch(this.publisherLookupUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      const data = await response.json()

      if (response.ok) {
        if (data.exists) {
          this.showToast(`Using existing publisher: ${data.publisher.name}`, "notice")
        } else if (data.publisher) {
          this.showToast("Publisher info retrieved via AI", "notice")
        }
      } else {
        this.showToast(data.error || "Failed to lookup publisher")
      }
    } catch (error) {
      console.error("Publisher lookup error:", error)
      this.showToast("An error occurred while looking up publisher")
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  async searchAuthorImage(event) {
    event.preventDefault()
    const row = event.target.closest('[data-book-lookup-target="authorRow"]')
    const input = row.querySelector('input[name="author_names[]"]')
    const name = input.value.trim()
    const button = event.target.closest('button')

    if (!name) {
      this.showToast("Please enter an author name first")
      return
    }

    if (!this.hasAuthorImageUrlValue) {
      this.showToast("Author image search not configured")
      return
    }

    // Show loading state
    button.disabled = true
    const originalContent = button.innerHTML
    button.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`

    try {
      const response = await fetch(this.authorImageUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showToast(`Author image found (${data.source})`, "notice")
      } else {
        this.showToast(data.error || "Could not find author image")
      }
    } catch (error) {
      console.error("Author image search error:", error)
      this.showToast("An error occurred while searching for author image")
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  getFirstAuthorName() {
    if (this.hasAuthorInputTarget) {
      return this.authorInputTargets[0]?.value?.trim() || ""
    }
    return ""
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  fillForm(data) {
    // Update cover image
    if (data.cover_image_url && this.hasCoverImageTarget) {
      this.coverImageTarget.src = data.cover_image_url
      this.coverImageTarget.classList.remove("hidden")
      if (this.hasCoverPlaceholderTarget) {
        this.coverPlaceholderTarget.classList.add("hidden")
      }
    }

    // Fill hidden fields
    if (this.hasSubtitleTarget && data.subtitle) {
      this.subtitleTarget.value = data.subtitle
    }
    if (this.hasIsbn13Target && data.isbn_13) {
      this.isbn13Target.value = data.isbn_13
    }
    if (this.hasIsbn10Target && data.isbn_10) {
      this.isbn10Target.value = data.isbn_10
    }
    if (this.hasPublisherNameTarget && data.publisher) {
      this.publisherNameTarget.value = data.publisher
    }
    if (this.hasPublicationDateTarget && data.publication_date) {
      this.publicationDateTarget.value = this.parseYear(data.publication_date)
    }
    if (this.hasPageCountTarget && data.page_count) {
      this.pageCountTarget.value = data.page_count
    }
    if (this.hasLanguageTarget && data.language) {
      this.languageTarget.value = data.language
    }
    if (this.hasDescriptionTarget && data.description) {
      // Handle both regular textarea and richtext contenteditable
      if (this.descriptionTarget.hasAttribute("contenteditable")) {
        this.descriptionTarget.innerHTML = this.escapeHtml(data.description)
        // Update the hidden input if it exists
        const hiddenInput = this.descriptionTarget.parentElement.querySelector('input[type="hidden"]')
        if (hiddenInput) {
          hiddenInput.value = data.description
        }
      } else {
        this.descriptionTarget.value = data.description
      }
    }
    if (this.hasCoverImageUrlTarget && data.cover_image_url) {
      this.coverImageUrlTarget.value = data.cover_image_url
    }
    if (this.hasCoverThumbnailUrlTarget && data.cover_thumbnail_url) {
      this.coverThumbnailUrlTarget.value = data.cover_thumbnail_url
    }

    // Update authors - handle multiple authors
    if (data.authors && this.hasAuthorsContainerTarget) {
      const authors = Array.isArray(data.authors) ? data.authors : [data.authors]
      this.setAuthors(authors)
    }

    // Show preview table
    this.showPreview(data)
  }

  showPreview(data) {
    if (!this.hasPreviewSectionTarget || !this.hasPreviewTableTarget) return

    const rows = []

    if (data.subtitle) {
      rows.push({ label: "Subtitle", value: data.subtitle })
    }
    if (data.isbn_13) {
      rows.push({ label: "ISBN-13", value: data.isbn_13 })
    }
    if (data.isbn_10) {
      rows.push({ label: "ISBN-10", value: data.isbn_10 })
    }
    if (data.publisher) {
      rows.push({ label: "Publisher", value: data.publisher })
    }
    if (data.publication_date) {
      rows.push({ label: "Publication Date", value: data.publication_date })
    }
    if (data.page_count) {
      rows.push({ label: "Pages", value: data.page_count })
    }
    if (data.language) {
      rows.push({ label: "Language", value: data.language })
    }
    if (data.description) {
      // Truncate long descriptions
      const desc = data.description.length > 200
        ? data.description.substring(0, 200) + "..."
        : data.description
      rows.push({ label: "Description", value: desc })
    }
    if (data.source) {
      rows.push({ label: "Source", value: data.source === "google_books" ? "Google Books" : "Open Library" })
    }

    if (rows.length === 0) {
      this.previewSectionTarget.classList.add("hidden")
      return
    }

    // Build table HTML
    this.previewTableTarget.innerHTML = rows.map(row => `
      <tr>
        <td class="py-2 pr-4 text-beige-500 font-medium whitespace-nowrap align-top">${this.escapeHtml(row.label)}</td>
        <td class="py-2 text-beige-800">${this.escapeHtml(row.value)}</td>
      </tr>
    `).join("")

    this.previewSectionTarget.classList.remove("hidden")
  }

  setAuthors(authors) {
    const container = this.authorsContainerTarget

    // Clear existing author rows
    container.innerHTML = ''

    // Add a row for each author
    if (authors.length === 0) {
      const row = this.createAuthorRow("")
      container.appendChild(row)
    } else {
      authors.forEach(name => {
        const row = this.createAuthorRow(name)
        container.appendChild(row)
      })
    }

    this.updateRemoveButtons()
  }

  parseYear(dateString) {
    if (!dateString) return ""
    const str = String(dateString)

    const yearMatch = str.match(/(\d{4})/)
    return yearMatch ? yearMatch[1] : ""
  }

  setSearchLoading(isLoading) {
    if (this.hasSearchButtonTarget) {
      this.searchButtonTarget.disabled = isLoading
    }
    if (this.hasSearchSpinnerTarget) {
      this.searchSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasSearchButtonTextTarget) {
      this.searchButtonTextTarget.textContent = isLoading ? "Searching..." : "Search"
    }
  }

  setAiLoading(isLoading) {
    if (isLoading) {
      showAiOverlay("AI Generating...")
    } else {
      hideAiOverlay()
    }

    if (this.hasAiButtonTarget) {
      this.aiButtonTarget.disabled = isLoading
    }
    if (this.hasAiSpinnerTarget) {
      this.aiSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasAiButtonTextTarget) {
      this.aiButtonTextTarget.textContent = isLoading ? "Generating..." : "AI Generate"
    }
  }

  async aiGenerateCover(event) {
    event.preventDefault()

    const title = this.titleTarget.value.trim()
    if (!title) {
      this.showToast("Please enter a book title first")
      return
    }

    if (!this.hasAiCoverUrlValue || !this.aiCoverUrlValue) {
      this.showToast("AI cover generation not configured")
      return
    }

    const author = this.getFirstAuthorName()
    const descEl = this.hasDescriptionTarget ? this.descriptionTarget : null
    const description = descEl
      ? (descEl.hasAttribute("contenteditable") ? descEl.textContent : descEl.value) || ""
      : ""

    this.setCoverLoading(true)

    try {
      const response = await fetch(this.aiCoverUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          title,
          author_name: author,
          description: description.trim()
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        if (this.hasCoverImageTarget) {
          this.coverImageTarget.src = data.image_url
          this.coverImageTarget.classList.remove("hidden")
        }
        if (this.hasCoverPlaceholderTarget) {
          this.coverPlaceholderTarget.classList.add("hidden")
        }
        if (this.hasCoverImageUrlTarget) {
          this.coverImageUrlTarget.value = data.image_url
        }
        this.showToast("Cover image generated!", "notice")
      } else if (response.status === 402 && data.redirect_url) {
        window.open(data.redirect_url, "_blank")
      } else {
        this.showToast(data.error || "Failed to generate cover image")
      }
    } catch (error) {
      console.error("AI cover generation error:", error)
      this.showToast("An error occurred while generating the cover")
    } finally {
      this.setCoverLoading(false)
    }
  }

  setCoverLoading(isLoading) {
    if (isLoading) {
      showAiOverlay("Generating Cover...")
    } else {
      hideAiOverlay()
    }

    if (this.hasCoverButtonTarget) {
      this.coverButtonTarget.disabled = isLoading
    }
    if (this.hasCoverSpinnerTarget) {
      this.coverSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasCoverButtonTextTarget) {
      this.coverButtonTextTarget.textContent = isLoading ? "Generating..." : "AI Cover"
    }
  }

  openCoverFilePicker(event) {
    event.preventDefault()
    if (this.hasCoverFileInputTarget) {
      this.coverFileInputTarget.click()
    }
  }

  // Opens the device camera (mobile/iPad) for the cover; the captured photo
  // flows through the same uploadCoverFile handler as a picked file.
  openCoverCamera(event) {
    event.preventDefault()
    if (this.hasCoverCameraInputTarget) {
      this.coverCameraInputTarget.click()
    }
  }

  uploadCoverFile(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    this.uploadCoverBlob(file).finally(() => {
      input.value = ""
    })
  }

  coverDragEnter(event) {
    if (!this.eventHasFiles(event)) return
    event.preventDefault()
    this.coverDragCounter = (this.coverDragCounter || 0) + 1
    this.showCoverDropOverlay(true)
  }

  coverDragOver(event) {
    if (!this.eventHasFiles(event)) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
  }

  coverDragLeave(event) {
    if (!this.eventHasFiles(event)) return
    event.preventDefault()
    this.coverDragCounter = Math.max(0, (this.coverDragCounter || 0) - 1)
    if (this.coverDragCounter === 0) this.showCoverDropOverlay(false)
  }

  coverDrop(event) {
    if (!this.eventHasFiles(event)) return
    event.preventDefault()
    this.coverDragCounter = 0
    this.showCoverDropOverlay(false)

    const file = event.dataTransfer?.files?.[0]
    if (!file) return
    this.uploadCoverBlob(file)
  }

  eventHasFiles(event) {
    const types = event.dataTransfer?.types
    if (!types) return false
    return Array.from(types).includes("Files")
  }

  showCoverDropOverlay(visible) {
    if (!this.hasCoverDropOverlayTarget) return
    this.coverDropOverlayTarget.classList.toggle("hidden", !visible)
    this.coverDropOverlayTarget.classList.toggle("flex", visible)
  }

  async uploadCoverBlob(file) {
    if (!file) return

    if (!file.type || !file.type.startsWith("image/")) {
      this.showToast("Please select an image file")
      return
    }

    if (!this.hasUploadCoverUrlValue || !this.uploadCoverUrlValue) {
      this.showToast("Upload endpoint not configured")
      return
    }

    this.setUploadLoading(true)

    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch(this.uploadCoverUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success && data.url) {
        if (this.hasCoverImageTarget) {
          this.coverImageTarget.src = data.url
          this.coverImageTarget.classList.remove("hidden")
        }
        if (this.hasCoverPlaceholderTarget) {
          this.coverPlaceholderTarget.classList.add("hidden")
        }
        if (this.hasCoverImageUrlTarget) {
          this.coverImageUrlTarget.value = data.url
        }
        this.showToast("Cover uploaded!", "notice")
      } else {
        this.showToast(data.error || "Failed to upload cover")
      }
    } catch (error) {
      console.error("Cover upload error:", error)
      this.showToast("An error occurred while uploading the cover")
    } finally {
      this.setUploadLoading(false)
    }
  }

  setUploadLoading(isLoading) {
    if (this.hasCoverButtonTarget) {
      this.coverButtonTarget.disabled = isLoading
    }
    if (this.hasCoverSpinnerTarget) {
      this.coverSpinnerTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasCoverButtonTextTarget) {
      this.coverButtonTextTarget.textContent = isLoading ? "Uploading..." : "Upload"
    }
  }
}
